import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { storableError } from '../util/errors';
import { getSaleListingTypeConfig } from '../util/collections';
import { addMarketplaceEntities } from './marketplaceData.duck';

/**
 * Nextmine: "List this piece for sale".
 *
 * Et samlingsstykke er ikke en vare - men samleren skal kunne skifte mening,
 * især når nogen lige har sagt at de er interesserede. I stedet for at bede
 * hende taste stykket ind igen, laver vi en kladde til en almindelig annonce
 * med stykkets titel, beskrivelse og fotos, og sender hende direkte til
 * pris-trinnet i wizarden.
 *
 * Stykket BLIVER i samlingen (Lines beslutning 2026-08-03: man har jo ejet
 * det). De to peger på hinanden via publicData, så vi bagefter kan vise "nu
 * til salg" og fortælle de interesserede besked om det.
 */

const MAX_COPIED_IMAGES = 10;

/** Den bredeste billedvariant vi har - annoncer fortjener det store foto. */
const widestVariantUrl = image => {
  const variants = Object.values(image?.attributes?.variants || {});
  const widest = variants.reduce(
    (best, v) => (v?.url && (!best || (v.width || 0) > (best.width || 0)) ? v : best),
    null
  );
  return widest?.url || null;
};

/**
 * Henter et af marketplacets egne billeder og uploader det igen som et nyt
 * billede. Sharetribes CDN svarer med `access-control-allow-origin: *`, så det
 * kan ske i browseren - ingen proxy nødvendig.
 */
const copyImage = (sdk, image, index) => {
  const url = widestVariantUrl(image);
  if (!url) {
    return Promise.resolve(null);
  }
  return window
    .fetch(url)
    .then(res => (res.ok ? res.blob() : null))
    .then(blob => {
      if (!blob || blob.type.indexOf('image/') !== 0) {
        return null;
      }
      const ext = blob.type === 'image/png' ? 'png' : 'jpg';
      const file = new File([blob], `collection-piece-${index + 1}.${ext}`, { type: blob.type });
      // NB: ingen `include` her - images.upload svarer 500 på det.
      return sdk.images.upload({ image: file }).then(res => res.data.data.id);
    })
    .catch(() => null);
};

const listPieceForSalePayloadCreator = (
  { piece, config },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  const saleTypeConfig = getSaleListingTypeConfig(config?.listing?.listingTypes);
  if (!saleTypeConfig) {
    return rejectWithValue(storableError(new Error('No sale listing type configured')));
  }

  const { listingType, transactionType = {} } = saleTypeConfig;
  const { alias, unitType } = transactionType;
  const pieceId = piece?.id?.uuid;
  const images = (piece?.images || []).slice(0, MAX_COPIED_IMAGES);

  return Promise.all(images.map((img, i) => copyImage(sdk, img, i)))
    .then(imageIds => {
      const uploaded = imageIds.filter(Boolean);
      return sdk.ownListings.createDraft(
        {
          title: piece?.attributes?.title || '',
          description: piece?.attributes?.description || '',
          ...(uploaded.length > 0 ? { images: uploaded } : {}),
          publicData: {
            listingType,
            ...(alias ? { transactionProcessAlias: alias } : {}),
            ...(unitType ? { unitType } : {}),
            // Sporet tilbage til samlingen - bruges når annoncen udgives.
            ...(pieceId ? { fromCollectionPiece: pieceId } : {}),
          },
        },
        { expand: true }
      );
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      const draftId = response.data.data.id;

      // Stykket peger tilbage, så samlingen kan vise "nu til salg".
      return sdk.ownListings
        .update({ id: piece.id, publicData: { forSaleListingId: draftId.uuid } })
        .catch(() => null)
        .then(() => draftId);
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const listPieceForSaleThunk = createAsyncThunk(
  'collectionSale/listPieceForSale',
  listPieceForSalePayloadCreator
);

export const listPieceForSale = (piece, config) => dispatch =>
  dispatch(listPieceForSaleThunk({ piece, config })).unwrap();

/**
 * Fortæller alle der har spurgt til stykket, at det nu er til salg. Uden det
 * ender interessen blindt: de får ingen besked om at deres spørgsmål blev til
 * en annonce, og en fremmed kan nå at købe den først.
 *
 * Marketplace API'et kan ikke filtrere transaktioner på listing, så vi henter
 * egne salg og sorterer selv. Samlinger er små; det er billigt nok.
 */
const notifyInterestedPayloadCreator = ({ pieceId, message }, { rejectWithValue, extra: sdk }) => {
  return sdk.transactions
    .query({ only: 'sale', include: ['listing'], perPage: 100 })
    .then(response => {
      const included = response.data.included || [];
      const listingIdsById = included.reduce((acc, entity) => {
        if (entity.type === 'listing') {
          acc[entity.id.uuid] = true;
        }
        return acc;
      }, {});

      const txIds = (response.data.data || [])
        .filter(tx => {
          const listingId = tx.relationships?.listing?.data?.id?.uuid;
          return !!listingId && listingId === pieceId && listingIdsById[listingId];
        })
        .map(tx => tx.id);

      return Promise.all(
        txIds.map(transactionId =>
          sdk.messages.send({ transactionId, content: message }).catch(() => null)
        )
      ).then(sent => sent.filter(Boolean).length);
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const notifyInterestedThunk = createAsyncThunk(
  'collectionSale/notifyInterested',
  notifyInterestedPayloadCreator
);

export const notifyInterestedInPiece = (pieceId, message) => dispatch =>
  dispatch(notifyInterestedThunk({ pieceId, message })).unwrap();

// ================ Slice ================ //

const initialState = {
  listPieceInProgress: false,
  listPieceError: null,
};

const collectionSaleSlice = createSlice({
  name: 'collectionSale',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(listPieceForSaleThunk.pending, state => {
        state.listPieceInProgress = true;
        state.listPieceError = null;
      })
      .addCase(listPieceForSaleThunk.fulfilled, state => {
        state.listPieceInProgress = false;
      })
      .addCase(listPieceForSaleThunk.rejected, (state, action) => {
        state.listPieceInProgress = false;
        state.listPieceError = action.payload;
      });
  },
});

export default collectionSaleSlice.reducer;
